import { Box, Typography, Button, TextField, Stack, IconButton, Checkbox } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import { useState } from "react";
import { addProductColors, updateProductColors, deleteProductColors } from "../../../../../services/Admin/ProductsMediaAndDetailsApi";

const ProductColorsTab = ({ productId, colors = [], onSuccess }) => {
  const [newColors, setNewColors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editingValues, setEditingValues] = useState({ color_ar: "", color_en: "", code: "" });
  const [rowSaving, setRowSaving] = useState(false);
  const [selectedColorIds, setSelectedColorIds] = useState([]);

  const handleAddRow = () => setNewColors((s) => [...s, { color_ar: "", color_en: "", code: "" }]);
  const handleRemove = (i) => setNewColors((s) => s.filter((_, idx) => idx !== i));
  const handleChange = (i, field, val) => setNewColors((s) => s.map((r, idx) => (idx === i ? { ...r, [field]: val } : r)));

  const toggleSelect = (id) => {
    if (!id) return;
    setSelectedColorIds((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  };

  const handleDeleteSingle = async (id) => {
    setError("");
    if (!productId) {
      setError("productId مطلوب.");
      return;
    }
    if (!id) return;
    setLoading(true);
    try {
      await deleteProductColors([id], { product_id: productId });
      setSelectedColorIds((s) => s.filter((x) => x !== id));
      if (onSuccess) await onSuccess();
    } catch (err) {
      setError(err?.message || "فشل حذف اللون");
    } finally {
      setLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    setError("");
    if (!productId) {
      setError("productId مطلوب.");
      return;
    }
    if (!selectedColorIds.length) {
      setError("اختر لونًا واحدًا على الأقل للحذف.");
      return;
    }
    setLoading(true);
    try {
      await deleteProductColors(selectedColorIds, { product_id: productId });
      setSelectedColorIds([]);
      if (onSuccess) await onSuccess();
    } catch (err) {
      setError(err?.message || "فشل حذف الألوان");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    setError("");
    if (!productId) {
      setError("productId مطلوب.");
      return;
    }
    if (!newColors.length) {
      setError("أضف لونًا واحدًا على الأقل.");
      return;
    }
    setLoading(true);
    try {
      await addProductColors(productId, newColors, { product_id: productId });
      setNewColors([]);
      if (onSuccess) await onSuccess();
    } catch (err) {
      setError(err?.message || "فشل إضافة الألوان");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography sx={{ fontWeight: 600, mb: 1 }}>ألوان المنتج</Typography>
      {(colors && colors.length) ? (
        <>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Button onClick={handleBulkDelete} disabled={!selectedColorIds.length || !productId || loading} size="small" variant="outlined" color="error">
              حذف المحدد ({selectedColorIds.length})
            </Button>
          </Stack>
          {colors.map((c, i) => {
           const isEditing = editingId === c.id;
           return (
             <Box key={c.id ?? i} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
               {!isEditing ? (
                 <>
                  <Checkbox checked={selectedColorIds.includes(c.id)} onChange={() => toggleSelect(c.id)} disabled={!c.id} size="small" />
                  <Box sx={{ width: 24, height: 24, borderRadius: "50%", bgcolor: c.code || "#ccc", border: "1px solid #ddd" }} />
                  <Typography sx={{ flex: 1 }}>{`${c.color_ar || c.color_en || ""} ${c.code ? `(${c.code})` : ""}`}</Typography>
                  <IconButton size="small" onClick={() => {
                    setEditingId(c.id);
                    setEditingValues({ color_ar: c.color_ar ?? "", color_en: c.color_en ?? "", code: c.code ?? "" });
                  }}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDeleteSingle(c.id)} aria-label="delete-existing" disabled={!c.id || loading || !productId}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                 </>
               ) : (
                 <>
                   <TextField size="small" label="اللون بالعربي" value={editingValues.color_ar} onChange={(e) => setEditingValues((p) => ({ ...p, color_ar: e.target.value }))} />
                   <TextField size="small" label="اللون بالإنجليزي" value={editingValues.color_en} onChange={(e) => setEditingValues((p) => ({ ...p, color_en: e.target.value }))} />
                   <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                     <input
                       type="color"
                       value={editingValues.code || "#000000"}
                       onChange={(e) => setEditingValues((p) => ({ ...p, code: e.target.value }))}
                       style={{ width: 40, height: 40, border: "none", cursor: "pointer", borderRadius: 4, padding: 0 }}
                     />
                     <TextField
                       size="small"
                       label="الكود"
                       value={editingValues.code}
                       onChange={(e) => setEditingValues((p) => ({ ...p, code: e.target.value }))}
                       sx={{ width: 120 }}
                     />
                   </Box>
                   <IconButton size="small" onClick={async () => {
                     setRowSaving(true);
                     setError("");
                     try {
                       const payload = [{
                         id: c.id,
                         color_ar: editingValues.color_ar,
                         color_en: editingValues.color_en,
                         code: editingValues.code ?? null,
                       }];
                       await updateProductColors(payload, { product_id: productId });
                       setEditingId(null);
                       setEditingValues({ color_ar: "", color_en: "", code: "" });
                       if (onSuccess) await onSuccess();
                     } catch (err) {
                       setError(err?.message || "فشل حفظ اللون");
                     } finally {
                       setRowSaving(false);
                     }
                   }} disabled={rowSaving || !productId}>
                     <SaveIcon fontSize="small" />
                   </IconButton>
                   <IconButton size="small" onClick={() => { setEditingId(null); setEditingValues({ color_ar: "", color_en: "", code: "" }); }}>
                     <CloseIcon fontSize="small" />
                   </IconButton>
                 </>
               )}
             </Box>
           );
          })}
        </>
       ) : (
         <Typography color="text.secondary">لا توجد ألوان</Typography>
       )}

      {/* إضافة ألوان جديدة */}
      <Box sx={{ mt: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <Button startIcon={<AddIcon />} onClick={handleAddRow} size="small" variant="contained">
            إضافة صف
          </Button>
          <Button onClick={handleSubmit} disabled={loading || !newColors.length || !productId} size="small" variant="outlined">
            {loading ? "جارٍ..." : "حفظ الألوان"}
          </Button>
        </Stack>

        {error ? <Typography color="error" sx={{ mb: 1 }}>{error}</Typography> : null}

        {newColors.map((r, idx) => (
          <Stack key={idx} direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <TextField size="small" label="اللون بالعربي" value={r.color_ar} onChange={(e) => handleChange(idx, "color_ar", e.target.value)} />
            <TextField size="small" label="اللون بالإنجليزي" value={r.color_en} onChange={(e) => handleChange(idx, "color_en", e.target.value)} />
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <input
                type="color"
                value={r.code || "#000000"}
                onChange={(e) => handleChange(idx, "code", e.target.value)}
                style={{ width: 40, height: 40, border: "none", cursor: "pointer", borderRadius: 4, padding: 0 }}
              />
              <TextField
                size="small"
                label="الكود"
                value={r.code}
                onChange={(e) => handleChange(idx, "code", e.target.value)}
                sx={{ width: 120 }}
              />
            </Box>
            <IconButton size="small" onClick={() => handleRemove(idx)} aria-label="remove">
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Stack>
        ))}
      </Box>
    </Box>
  );
};

export default ProductColorsTab;